import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import pb from '../../services/pocketbase';
import './AdminDashboard.css';
import ProductManager from './ProductManager';
import EventManager from './EventManager';
import CategoryManager from './CategoryManager';
import ReviewManager from './ReviewManager';
import SupportChatManager from './SupportChatManager';
import ContactMessagesManager from './ContactMessagesManager';
import UserManager from './UserManager';
import AccountPage from '../../pages/Account/AccountPage';
import AttributeManager from './AttributeManager/AttributeManager';
import DashboardOverview from '../DashboardOverview/DashboardOverview';
import NotificationSystem from '../NotificationSystem/NotificationSystem';
import { FaHome, FaEnvelope, FaGem, FaCalendarAlt, FaBars } from 'react-icons/fa';

function AdminDashboard() {
  const navigate = useNavigate();
  const { user, isLoggedIn, logout } = useAuth();

  const [activeTab, setActiveTab] = useState('overview');
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [categories, setCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [reviews, setReviews] = useState([]);
  const [events, setEvents] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [users, setUsers] = useState([]);
  const [colors, setColors] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [pearlTypes, setPearlTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const navRef = useRef(null);

  // Suppression de abortControllerRef
  // const abortControllerRef = useRef(null);

  const fetchAllData = useCallback(async () => {
    setLoading(true);
    try {
      const promises = [
        pb.collection('categories').getFullList({ sort: '-created', requestKey: 'categories' }),
        pb.collection('products').getFullList({ sort: '-created', expand: 'category,color,material,pearl_type', requestKey: 'products' }),
        pb.collection('reviews').getFullList({ sort: '-created', expand: 'user', requestKey: 'reviews' }),
        pb.collection('events').getFullList({ sort: '-start_date', requestKey: 'events' }),
        pb.collection('contact_messages').getFullList({ sort: '-created', requestKey: 'contact_messages' }),
        pb.collection('users').getFullList({
          sort: '-created',
          requestKey: 'users',
          skipTotal: true
        }),
        pb.collection('colors').getFullList({ requestKey: 'colors' }),
        pb.collection('materials').getFullList({ requestKey: 'materials' }),
        pb.collection('pearl_types').getFullList({ requestKey: 'pearl_types' }),
      ];

      const [
        categoriesData,
        productsData,
        reviewsData,
        eventsData,
        contactMessagesData,
        usersData,
        colorsData,
        materialsData,
        pearlTypesData
      ] = await Promise.all(promises);

      setCategories(categoriesData);
      setProducts(productsData);
      setReviews(reviewsData);
      setEvents(eventsData);
      setContactMessages(contactMessagesData);
      setUsers(usersData);
      setColors(colorsData);
      setMaterials(materialsData);
      setPearlTypes(pearlTypesData);

    } catch (error) {
      // La nouvelle version du SDK de PocketBase gère mieux les AbortErrors.
      // On vérifie le nom de l'erreur pour s'assurer que ce n'est pas un problème réel.
      if (error.name !== 'AbortError') {
        console.error("Erreur lors de la récupération des données:", error);
        alert("Impossible de charger les données du dashboard.");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/');
      return;
    }
    // Lancement de la récupération des données
    fetchAllData();

    // Suppression de la fonction de nettoyage qui annulait les requêtes
    // en mode Strict. PocketBase gère désormais cela via requestKey.
    // return () => {
    //   if (abortControllerRef.current) {
    //     abortControllerRef.current.abort();
    //   }
    // };
  }, [isLoggedIn, navigate, fetchAllData]);

  // Effet pour calculer et définir la largeur fixe de la barre de navigation
  useEffect(() => {
    if (navRef.current) {
      const nav = navRef.current;
      // Obtenir tous les boutons de navigation
      const buttons = nav.querySelectorAll('button');
      let maxWidth = 0;

      // Trouver la largeur maximale nécessaire
      buttons.forEach(button => {
        // Créer un élément temporaire pour mesurer la largeur sans contraintes
        const temp = document.createElement('div');
        temp.style.position = 'absolute';
        temp.style.visibility = 'hidden';
        temp.style.whiteSpace = 'nowrap';
        temp.style.padding = '1rem';
        temp.style.fontSize = window.getComputedStyle(button).fontSize;
        temp.style.fontFamily = window.getComputedStyle(button).fontFamily;
        temp.textContent = button.textContent;
        document.body.appendChild(temp);

        // Mettre à jour la largeur maximale si nécessaire
        maxWidth = Math.max(maxWidth, temp.offsetWidth);
        document.body.removeChild(temp);
      });

      // Ajouter une marge pour le padding et la bordure
      const padding = 40; // 20px de chaque côté
      nav.style.width = `${maxWidth + padding}px`;

      // Stocker la largeur calculée pour une utilisation ultérieure
      nav.dataset.calculatedWidth = maxWidth + padding;
    }
  }, [activeTab]); // Se recalculer si l'onglet actif change

  useEffect(() => {
    // Bloque le défilement de la page principale lorsque le tableau de bord est actif
    const originalStyle = window.getComputedStyle(document.body).overflow;
    document.body.style.overflow = 'hidden';

    // Rétablit le défilement lorsque le composant est démonté
    return () => {
      document.body.style.overflow = originalStyle;
    };
  }, []); // Le tableau de dépendances est vide pour que l'effet s'exécute uniquement au montage et au démontage


  const handleDataUpdate = useCallback(() => {
    fetchAllData();
  }, [fetchAllData]);

  const handleProductDeleted = (productId) => {
    setProducts(prev => prev.filter(p => p.id !== productId));
  };

  const handleCategoryDeleted = (categoryId) => {
    setCategories(prev => prev.filter(c => c.id !== categoryId));
    fetchAllData();
  };

  const handleReviewApproved = (updatedReview) => {
    setReviews(prev => prev.map(r => r.id === updatedReview.id ? updatedReview : r));
  };

  const handleReviewDeleted = (reviewId) => {
    setReviews(prev => prev.filter(r => r.id !== reviewId));
  };

  const handleEventDeleted = (eventId) => {
    setEvents(prev => prev.filter(e => e.id !== eventId));
  };

  const handleLogout = () => {
    logout();
  };

  const renderSettings = () => (
    <div>
      <h4>Paramètres</h4>
      <p>Utilisateur : {user?.email}</p>
      <button onClick={handleLogout} className="logout-btn">Déconnexion</button>
    </div>
  );

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    if (window.innerWidth <= 768) {
      setIsNavOpen(false);
    }
  };

  const renderContent = () => {
    if (loading) {
      return <div><h4>Chargement des données...</h4></div>;
    }
    switch (activeTab) {
      case 'overview':
        return <DashboardOverview
          products={products}
          users={users}
          reviews={reviews}
          categories={categories}
          events={events}
          handleNavClick={handleNavClick}
        />;
      case 'products':
        return <ProductManager
          products={products}
          categories={categories}
          colors={colors}
          materials={materials}
          pearlTypes={pearlTypes}
          onProductAdded={handleDataUpdate}
          onProductUpdated={handleDataUpdate}
          onProductDeleted={handleProductDeleted}
          loading={loading}
        />;
      case 'categories':
        return <CategoryManager
          categories={categories}
          onCategoryAdded={handleDataUpdate}
          onCategoryUpdated={handleDataUpdate}
          onCategoryDeleted={handleCategoryDeleted}
        />;
      case 'filters':
        return (
          <div>
            <AttributeManager
              title="Couleurs"
              collectionName="colors"
              items={colors}
              onUpdate={handleDataUpdate}
            />
            <hr className="my-6" />
            <AttributeManager
              title="Matériaux"
              collectionName="materials"
              items={materials}
              onUpdate={handleDataUpdate}
            />
            <hr className="my-6" />
            <AttributeManager
              title="Types de perles"
              collectionName="pearl_types"
              items={pearlTypes}
              onUpdate={handleDataUpdate}
            />
          </div>
        );
      case 'agenda':
        return <EventManager
          events={events}
          onEventAdded={handleDataUpdate}
          onEventUpdated={handleDataUpdate}
          onEventDeleted={handleEventDeleted}
        />;
      case 'reviews':
        return <ReviewManager
          reviews={reviews}
          onReviewApproved={handleReviewApproved}
          onReviewDeleted={handleReviewDeleted}
        />;
      case 'contact':
        return <ContactMessagesManager />;
      case 'users':
        return <UserManager users={users} onUserUpdated={handleDataUpdate} />;
      case 'my-account':
        return <div className="account-page-container-admin"><AccountPage /></div>;
      case 'settings':
        return renderSettings();
      default: return null;
    }
  };

  return (
    <div className={`admin-dashboard ${isNavOpen ? 'nav-open' : ''}`}>
      <NotificationSystem />
      <div className="dashboard-body">
        <button className={`nav-toggle-btn ${isNavOpen ? 'open' : ''}`} onClick={() => setIsNavOpen(!isNavOpen)}>
          <span className="icon-bar"></span>
          <span className="icon-bar"></span>
          <span className="icon-bar"></span>
        </button>
        <div className="dashboard-nav" ref={navRef} style={{ minWidth: '200px' }}>
          <div>
            <button onClick={() => handleNavClick('overview')} className={activeTab === 'overview' ? 'active' : ''}>Vue d'ensemble</button>
            <button onClick={() => handleNavClick('products')} className={activeTab === 'products' ? 'active' : ''}>Produits</button>
            <button onClick={() => handleNavClick('categories')} className={activeTab === 'categories' ? 'active' : ''}>Catégories</button>
            <button onClick={() => handleNavClick('filters')} className={activeTab === 'filters' ? 'active' : ''}>Filtres</button>
            <button onClick={() => handleNavClick('agenda')} className={activeTab === 'agenda' ? 'active' : ''}>Agenda</button>
            <button onClick={() => handleNavClick('reviews')} className={activeTab === 'reviews' ? 'active' : ''}>Avis</button>
            <button onClick={() => handleNavClick('contact')} className={activeTab === 'contact' ? 'active' : ''}>
              Messages
              {contactMessages.filter(m => m.status === 'nouveau').length > 0 && (
                <span className="nav-badge pulse">
                  {contactMessages.filter(m => m.status === 'nouveau').length}
                </span>
              )}
            </button>
            <button onClick={() => handleNavClick('users')} className={activeTab === 'users' ? 'active' : ''}>Utilisateurs</button>
            <button onClick={() => handleNavClick('my-account')} className={activeTab === 'my-account' ? 'active' : ''}>Mon Compte</button>
            <button onClick={() => handleNavClick('settings')} className={activeTab === 'settings' ? 'active' : ''}>Paramètres</button>
          </div>
        </div>
        <div className="dashboard-content">
          {isLoggedIn && user?.role === 'admin' ? renderContent() : <p>Accès non autorisé.</p>}
        </div>

        {/* Barre de navigation mobile - Proposition 1 (App Style) */}
        <div className="admin-mobile-bottom-nav">
          <button
            className={activeTab === 'overview' ? 'active' : ''}
            onClick={() => handleNavClick('overview')}
          >
            <FaHome />
            <span>Vue</span>
          </button>
          <button
            className={activeTab === 'contact' ? 'active' : ''}
            onClick={() => handleNavClick('contact')}
          >
            <FaEnvelope />
            <span>Messages</span>
            {contactMessages.filter(m => m.status === 'nouveau').length > 0 && (
              <span className="mobile-nav-badge pulse">
                {contactMessages.filter(m => m.status === 'nouveau').length}
              </span>
            )}
          </button>
          <button
            className={activeTab === 'products' ? 'active' : ''}
            onClick={() => handleNavClick('products')}
          >
            <FaGem />
            <span>Produits</span>
          </button>
          <button
            className={activeTab === 'agenda' ? 'active' : ''}
            onClick={() => handleNavClick('agenda')}
          >
            <FaCalendarAlt />
            <span>Agenda</span>
          </button>
          <button
            className={isNavOpen ? 'active' : ''}
            onClick={() => setIsNavOpen(!isNavOpen)}
          >
            <FaBars />
            <span>Menu</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;